import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Menu,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import { RecruitmentCandidate } from '../../types/worker';
import { fetchCandidates, addCandidate, updateCandidateStage } from '../../services/workerService';

interface RecruitmentTabProps {
  farmId: string;
}

const RecruitmentTab: React.FC<RecruitmentTabProps> = ({ farmId }) => {
  const [candidates, setCandidates] = useState<RecruitmentCandidate[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  
  const [newCandidate, setNewCandidate] = useState<Partial<RecruitmentCandidate>>({
    farmId,
    stage: 'applied',
    name: '',
    email: '',
    roleApplied: ''
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      const data = await fetchCandidates(farmId);
      setCandidates(data);
    } catch (error) {
      console.error('Error loading candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCandidate = async () => {
    try {
      if (!newCandidate.name || !newCandidate.roleApplied) return;
      
      await addCandidate({ ...newCandidate, farmId });
      setOpen(false);
      loadData();
      setNewCandidate({ farmId, stage: 'applied', name: '', email: '', roleApplied: '' });
    } catch (error) {
      console.error('Error adding candidate:', error);
    }
  };

  const handleStageChange = async (newStage: string) => {
    if (selectedCandidateId) {
      try {
        await updateCandidateStage(selectedCandidateId, newStage);
        loadData();
      } catch (error) {
        console.error('Error updating stage:', error);
      }
    }
    setAnchorEl(null);
    setSelectedCandidateId(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCandidateId(id);
  };

  const stages = ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Recruitment Pipeline</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Candidate
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto', pb: 2 }}>
        <Box sx={{ display: 'flex', minWidth: '1000px', gap: 2 }}>
          {stages.map((stage) => (
            <Paper 
              key={stage} 
              sx={{ 
                width: 280, 
                minHeight: 400, 
                p: 2, 
                bgcolor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {stage}
                </Typography>
                <Chip 
                  label={candidates.filter(c => c.stage === stage).length} 
                  size="small" 
                  color="primary" 
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {candidates
                  .filter(c => c.stage === stage)
                  .map(candidate => (
                    <Card key={candidate.id} elevation={1}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {candidate.name}
                          </Typography>
                          <IconButton size="small" onClick={(e) => openMenu(e, candidate.id)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <WorkIcon fontSize="inherit" /> {candidate.roleApplied}
                        </Typography>
                        {candidate.email && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <EmailIcon fontSize="inherit" /> {candidate.email}
                          </Typography>
                        )}
                        {candidate.phone && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <PhoneIcon fontSize="inherit" /> {candidate.phone}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem disabled>Move to...</MenuItem>
        <Divider />
        {stages.map((stage) => (
          <MenuItem key={stage} onClick={() => handleStageChange(stage)}>
            {stage.charAt(0).toUpperCase() + stage.slice(1)}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Candidate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Full Name"
                fullWidth
                value={newCandidate.name || ''}
                onChange={(e) => setNewCandidate({ ...newCandidate, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Role Applied For"
                fullWidth
                value={newCandidate.roleApplied || ''}
                onChange={(e) => setNewCandidate({ ...newCandidate, roleApplied: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={newCandidate.email || ''}
                onChange={(e) => setNewCandidate({ ...newCandidate, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Phone"
                fullWidth
                value={newCandidate.phone || ''}
                onChange={(e) => setNewCandidate({ ...newCandidate, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={newCandidate.notes || ''}
                onChange={(e) => setNewCandidate({ ...newCandidate, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCandidate}>Add Candidate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecruitmentTab;
